import React, { useMemo } from 'react';
import { useNavigation } from '../contexts/NavigationProvider';
import { useAuth } from '../contexts/AuthProvider';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import MapPinIcon from './icons/MapPinIcon';
import StarIcon from './icons/StarIcon';
import type { User } from '../types';

const EndorserListPage: React.FC = () => {
    const { viewingEndorserListOwner, handleNavigation, handleViewProfile, navigationOrigin } = useNavigation();
    const { users } = useAuth();

    const userMap = useMemo(() => new Map(users.map(u => [u.handle, u])), [users]);

    const endorsers = useMemo(() => {
        if (!viewingEndorserListOwner) return [];
        return (viewingEndorserListOwner.endorsedBy || [])
            .map(h => userMap.get(h))
            .filter((u): u is User => !!u);
    }, [viewingEndorserListOwner, userMap]);

    // Geography Distribution Logic for Endorsers
    const geoDistribution = useMemo(() => {
        const counts: Record<string, number> = {};
        endorsers.forEach(e => {
            const area = e.region ? `${e.region}, ${e.country || ''}` : (e.country || e.city || 'Global');
            counts[area] = (counts[area] || 0) + 1;
        });
        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5);
    }, [endorsers]);

    if (!viewingEndorserListOwner) return null;

    const ownerName = viewingEndorserListOwner.name;

    return (
        <div className="space-y-6 font-urbanist animate-in fade-in duration-500">
            <button onClick={() => handleNavigation(navigationOrigin || 'feed')} className="flex items-center space-x-2 text-brand-muted hover:text-white font-semibold transition-colors">
                <ArrowLeftIcon className="h-5 w-5" />
                <span>Back</span>
            </button>

            <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="flex items-center space-x-4">
                        <div className="bg-brand-orange/10 p-3 rounded-xl">
                            <StarIcon className="h-8 w-8 text-brand-orange" />
                        </div>
                        <div>
                            <h2 className="text-3xl font-extrabold text-white tracking-tight uppercase">Endorsers of {ownerName}</h2>
                            <p className="text-brand-muted text-sm uppercase font-bold tracking-widest">{endorsers.length} verified professional recommendations</p>
                        </div>
                    </div>
                    
                    {geoDistribution.length > 0 && (
                        <div className="bg-gray-800/40 p-4 rounded-xl border border-gray-700 min-w-[240px]">
                            <h4 className="text-[10px] font-black text-brand-orange uppercase tracking-[0.2em] mb-3">Network Hubs</h4>
                            <div className="space-y-2">
                                {geoDistribution.map(([area, count]) => (
                                    <div key={area} className="flex justify-between items-center text-xs font-bold">
                                        <span className="text-gray-300 truncate mr-4 uppercase">{area}</span>
                                        <span className="text-white bg-gray-700 px-2 py-0.5 rounded-full">{count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {endorsers.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {endorsers.map(endorser => {
                        const endorserLoc = endorser.city ? `${endorser.city}, ${endorser.country || ''}` : 'Global';
                        return (
                            <div 
                                key={endorser.handle} 
                                onClick={() => handleViewProfile(endorser)} 
                                className="bg-brand-surface p-4 rounded-xl border border-gray-800 hover:border-brand-orange/50 transition-all cursor-pointer group flex items-center gap-4"
                            >
                                <img src={endorser.avatarUrl} className="h-16 w-16 rounded-full border-2 border-gray-700 group-hover:border-brand-orange/50 transition-colors" alt="" />
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-white text-lg truncate group-hover:text-brand-orange transition-colors uppercase tracking-tight">{endorser.name}</p>
                                    <p className="text-xs text-brand-muted font-mono">@{endorser.handle}</p>
                                    <p className="text-[10px] text-brand-orange font-black mt-1 uppercase tracking-widest truncate">{endorser.title}</p>
                                    <div className="flex items-center text-brand-muted mt-2">
                                        <MapPinIcon className="h-3.5 w-3.5 mr-1 text-brand-orange" />
                                        <span className="text-[10px] font-bold tracking-wider truncate uppercase">{endorserLoc}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="bg-brand-surface rounded-xl p-20 text-center border-2 border-dashed border-gray-800">
                    <StarIcon className="h-16 w-16 text-gray-700 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2">No endorsements yet</h3>
                    <p className="text-brand-muted">Quality recommendations take time. Check back later!</p>
                </div>
            )}
        </div>
    );
};

export default EndorserListPage;